import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronLeft, ChevronRight, User, LogOut, ShieldCheck, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TopNav = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="sticky top-0 z-30 bg-[#0a0a0b]/85 backdrop-blur-md border-b border-white/5">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-neutral-300 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate(1)}
            className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-neutral-300 hover:text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="relative ml-4 hidden md:block">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              placeholder="Search DJs, mixes, tracks, artists"
              className="pl-10 pr-4 py-2 w-[380px] rounded-full bg-white/5 border border-white/10 text-sm text-neutral-200 placeholder:text-neutral-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/8 transition-colors"
            />
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-6 text-sm text-neutral-300">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <Link to="/mixes" className="hover:text-white transition-colors">Mixes</Link>
          <Link to="/mixtapes" className="hover:text-white transition-colors">Mixtapes</Link>
          <Link to="/tracks" className="hover:text-white transition-colors">Tracks</Link>
          <Link to="/charts" className="hover:text-white transition-colors">Charts</Link>
          <Link to="/djs" className="hover:text-white transition-colors">DJs</Link>
          <Link to="/record-pool" className="text-orange-400 hover:text-orange-300 transition-colors">Record Pool</Link>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/submit"
                className="hidden sm:inline-flex px-4 py-2 text-xs font-semibold rounded-full bg-white text-black hover:bg-neutral-200 transition-colors"
              >
                Submit Music
              </Link>
              <button className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-neutral-300 hover:text-white transition-colors">
                <Bell className="w-4 h-4" />
              </button>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center text-white text-xs font-semibold">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs text-white font-medium hidden sm:inline">{user.name.split(' ')[0]}</span>
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-lg bg-[#141416] border border-white/10 shadow-xl py-2 z-50">
                    <div className="px-4 py-2 border-b border-white/5">
                      <div className="text-sm text-white truncate">{user.name}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{user.email}</div>
                      <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-orange-500/15 text-orange-300 border border-orange-500/25">
                        {user.role.replace('_', ' ')}
                      </div>
                    </div>
                    {user.role === 'super_admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-200 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <ShieldCheck className="w-4 h-4 text-orange-400" /> Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/submit'); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-200 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <User className="w-4 h-4" /> Submit Music
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-200 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-full bg-white text-black hover:bg-neutral-200 transition-colors"
            >
              <LogIn className="w-4 h-4" /> Sign In
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopNav;
